/**
 * Hydrate blueprint mountedFolders from the main-process registry on app init.
 *
 * The renderer scriptBrowserStore persists its own mountedFolders to
 * localStorage, but a blueprint created headlessly (MCP/CLI) or cloned on a
 * fresh machine won't be in that persisted state. The registry is the
 * cross-process source of truth, so on startup we:
 *   1. list the registry,
 *   2. for any registry folderPath not already a blueprint in the store,
 *      read its manifest and `markFolderAsBlueprint` (projection in).
 *
 * `markFolderAsBlueprint` re-writes-through to registry-add, which dedupes by
 * folderPath, so this is idempotent. Best-effort: any failure leaves the
 * persisted store untouched.
 */
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import {
  listBlueprintRegistry,
  readBlueprintMetadata,
} from '../../database-blueprint/services/databaseBlueprintApi';

const normalize = (p: string): string => p.replace(/\\/g, '/').trim();

export async function hydrateBlueprintRegistry(): Promise<void> {
  const entries = await listBlueprintRegistry();
  if (entries.length === 0) return;

  const store = useScriptBrowserStore.getState();
  const ws = store.activeWorkspaceId
    ? store.workspaces.find((w) => w.id === store.activeWorkspaceId)
    : null;
  const existing = ws ? ws.mountedFolders : store.defaultWorkspaceState.mountedFolders;
  const knownBlueprintPaths = new Set(
    existing.filter((f) => f.kind === 'blueprint').map((f) => normalize(f.directoryPath)),
  );

  for (const entry of entries) {
    if (knownBlueprintPaths.has(normalize(entry.folderPath))) continue;
    const meta = await readBlueprintMetadata({ folderPath: entry.folderPath });
    if (!meta.success) continue; // reconcile on the main side will prune dead ones
    useScriptBrowserStore.getState().markFolderAsBlueprint(entry.folderPath, entry.label, {
      version: 1,
      linkedDbs: meta.metadata.linkedDbs.map((l) => ({
        label: l.label,
        connectionId: l.connectionId,
        connectionDisplayName: l.connectionDisplayName,
        database: l.database,
        engine: l.engine,
        lastSyncAt: l.lastSyncAt,
      })),
      excludedObjects: meta.metadata.excludedObjects ?? [],
      ...(meta.metadata.activeLinkLabel ? { activeLinkLabel: meta.metadata.activeLinkLabel } : {}),
    });
  }
}
